// react
import type { ReactNode } from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link, useMatches, useParams } from '@tanstack/react-router';

// material ui
import { Breadcrumbs as MuiBreadcrumbs, Typography } from '@mui/material';

// components
import { useBreadcrumbContext } from '@/app/BreadcrumbContext';

interface AppBreadcrumbsProps {
  /** Optional trailing actions rendered right-aligned next to the trail. */
  children?: ReactNode;
}

/**
 * Reads the current TanStack Router matches and renders a MUI breadcrumb
 * trail from each match's `staticData.crumb` (see
 * `src/types/tanstack-router.d.ts`), or a dynamic override registered via
 * `useBreadcrumb` (see `src/hooks/useBreadcrumb.ts`) - overrides win when
 * both are present, and can supply a label for routes with no static
 * `crumb` at all. Matches with neither are skipped - this naturally
 * excludes pathless layout routes and dynamic id segments ($accountId,
 * $unitId, etc.) without any extra filtering logic.
 *
 * Rendered right after the account selector in the AppBar (Azure DevOps
 * "repos" style), so the trail always opens with a leading "/" separator to
 * visually continue from the selected account.
 */
export default function AppBreadcrumbs({ children }: AppBreadcrumbsProps) {
  const matches = useMatches();
  const params = useParams({ strict: false });
  const { overrides } = useBreadcrumbContext();

  // Crumb keys are 'namespace:key' i18next strings, but the app's i18n
  // config only preloads a fixed set of namespaces (see src/i18n/i18n.js).
  // useTranslation() must be told about every namespace referenced by a
  // crumb so it actually fetches + suspends on it - otherwise t() just
  // returns the raw key for any namespace it wasn't told to load.
  const crumbNamespaces = Array.from(
    new Set(
      matches
        .map((match) => match.staticData.crumb)
        .filter((crumb): crumb is string => typeof crumb === 'string')
        .filter((crumb) => crumb.includes(':'))
        .map((crumb) => crumb.split(':')[0]),
    ),
  );
  const { t } = useTranslation(crumbNamespaces);

  const crumbs = matches
    .map((match) => {
      // A dynamic override (see `useBreadcrumb`) wins over the route's
      // static `crumb` - it can also supply a label for routes that have
      // no static crumb at all (e.g. an `$energyGroupId` route, where
      // showing the raw id wouldn't make sense).
      const override = overrides[match.id];
      const { crumb } = match.staticData;
      if (!override && !crumb) return null;

      const label =
        override ??
        (typeof crumb === 'function' ? crumb({ params }) : t(crumb));
      if (!label) return null;

      return { id: match.id, label, pathname: match.pathname };
    })
    .filter((crumb): crumb is NonNullable<typeof crumb> => Boolean(crumb));

  if (!crumbs.length && !children) return null;

  return (
    <Typography
      component="div"
      variant="body2"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
        minWidth: 0,
        color: 'text.disabled',
      }}
    >
      <Typography variant="body1" color="text.secondary">
        /
      </Typography>

      <MuiBreadcrumbs
        separator="/"
        sx={{
          minWidth: 0,
          '& .MuiBreadcrumbs-ol': { flexWrap: 'nowrap' },
          '& .MuiBreadcrumbs-li': { minWidth: 0 },
        }}
      >
        {crumbs.map((crumb, index) =>
          index === crumbs.length - 1 ? (
            <Typography
              key={crumb.id}
              component="span"
              variant="body1"
              fontWeight={600}
              noWrap
              color="text.primary"
            >
              {crumb.label}
            </Typography>
          ) : (
            <Typography
              key={crumb.id}
              component={Link}
              to={crumb.pathname}
              variant="body1"
              noWrap
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'text.primary' },
              }}
            >
              {crumb.label}
            </Typography>
          ),
        )}
      </MuiBreadcrumbs>
      {children}
    </Typography>
  );
}
